import { z } from 'zod';
import { personIdSchema } from '../people/person.js';
import { fileCategorySchema } from './categories.js';
import { fileIdSchema } from './stored-file.js';
/**
 * Correcting a misfiled file, the app's half (ADR-009 §3a steps 1–2, #194).
 *
 * The correction is FORWARD ONLY. The misfiled FileRecord is never edited and its bytes are never
 * touched by the app: an HR Admin records a `file-correction` (append-only), and the same bytes are
 * re-committed through the store path to the store, category and person they should have had, as
 * a new FileRecord with `correctionOf`. The list and viewer hide the misfiled record by derivation
 * (`correctedAwayFileIds`). Removing the wrong version is the break-glass runbook's job
 * (docs/mvp/misfiled-file-correction.md), run by a person outside the app, never by app code.
 */
/**
 * `POST /api/people/{personId}/files/{fileId}/corrections`. `.strict()`: the body names what the
 * file should have been and why, and nothing that could point anywhere.
 *
 * `operationId` makes the request idempotent: every id — the correction, the corrected FileRecord,
 * the envelope, the audit row — is derived from it, so a retried click meets its own first attempt.
 */
export const fileCorrectionRequestSchema = z
    .object({
    operationId: z.string().uuid(),
    /** The category the file should have had. Omitted when only the person was wrong. */
    targetCategory: fileCategorySchema.optional(),
    /** The person the file belongs to. Omitted when only the category was wrong. */
    targetPersonId: personIdSchema.optional(),
    /** Kept on the correction document only. Audit rows carry no free text (ADR-009 §5). */
    reason: z.string().trim().min(1).max(1000),
})
    .strict()
    .refine((body) => body.targetCategory !== undefined || body.targetPersonId !== undefined, {
    message: 'a correction names the correct category, the correct person, or both',
    path: ['targetCategory'],
});
export const fileCorrectionResponseSchema = z
    .object({
    correctionId: z.string().uuid(),
    misfiledFileId: fileIdSchema,
    /** The FileRecord the re-commit writes. It appears in the list once the effect has run. */
    correctedFileId: fileIdSchema,
    /** Always `queued`: the re-commit is an effect, like every other store (CLAUDE.md rule 5). */
    status: z.literal('queued'),
})
    .strict();
export function correctionTarget(misfiled, correction) {
    return { personId: correction.targetPersonId ?? misfiled.personId, category: correction.targetCategory ?? misfiled.category };
}
/** True when a correction would re-commit the file exactly where it already is — not a correction. */
export function correctsNothing(misfiled, target) {
    return target.personId === misfiled.personId && target.category === misfiled.category;
}
/**
 * The `causedBy.instanceId` of a correction's envelope. A correction belongs to no step, so — as
 * for an HR-composed email (#62) — the thing it records is its cause.
 */
export function correctionCause(correctionId) {
    return `file-correction:${correctionId}`;
}
//# sourceMappingURL=correction.js.map